import React, { CSSProperties, useEffect, useState } from 'react';
import Select, {
  components,
  ControlProps,
  DropdownIndicatorProps,
} from 'react-select';
import styled from 'styled-components';
import { getRDSColor } from '@pass-frontend/utility/style-helpers';

import { ReactComponent as DropdownArrow } from './assets/system-arrow-down.svg';
import { Menu } from './menu';
import { Option } from './option';

/**
 * Adds single/Multi Select dropdown menu
 * @example
 *  <SelectDropdown
 *    options={options_array}
 *    label="Dropdown_name"
 *    isMulti={true}
 *  />
 */

export interface SelectOption<T extends string> {
  label: string;
  value: T;
}

export interface SelectDropdownProps<T extends string> {
  options: SelectOption<T>[];
  isMulti?: boolean;
  label: string;
  defaultSelectedOption?: SelectOption<T> | SelectOption<T>[];
  className?: string;
  // below is for radio and isMulti dropdown
  onApply?: (option: SelectOption<T> | SelectOption<T>[]) => void;
  onClear?: (option: SelectOption<T> | SelectOption<T>[]) => void;
  onApplyMulti?: (options: SelectOption<T>[]) => void;
  onClearMulti?: (options: SelectOption<T>[]) => void;
  refer?: any;
  blurInputOnSelect?: boolean;
}

// `z-index` on `select-dropdown__menu` Need to beat out some z-index 1s, such as on the online-fitness page
const StyledSelect = styled(Select)`
  .select-dropdown__control {
    border-color: ${getRDSColor('bodyText')};

    &.control {
      border-color: ${getRDSColor('primaryColor')};
    }

    .select-dropdown__indicator {
      padding-right: 0;
    }
  }

  .select-dropdown__menu {
    z-index: 2;

    .select-dropdown__option {
      span {
        white-space: nowrap;
      }

      .select-dropdown__option {
        &:not(:last-child) {
          margin-bottom: 24px;
        }
      }

      .rds-link-ext-inline {
        margin-top: 10px;
      }

      .select-dropdown__menu-list {
        // fixed height to show 5 options and scroll
        max-height: 224px;
        overflow-y: scroll;
        padding: 8px 9px 0 0;
      }
    }
  }
`;

const Control = (props: ControlProps) => {
  const { value, name } = props.selectProps;
  /** This adds the css classes conditionally when there are any options selected.
   * selectProps.value is the prop available in react-select library which returns the selected items.
   **/
  const stylingClassesForWhenAnyOptionIsSelected =
    Array.isArray(value) && value.length !== 0
      ? 'control has-rds-text-primary'
      : '';
  const selectedOptionsString =
    Array.isArray(value) && value.length > 0
      ? ` ${value.map((option: any) => option.label).toString()} are selected`
      : '';

  return (
    <div
      aria-label={`${name} select is focused, press Down to open the menu${selectedOptionsString}`}
    >
      <components.Control
        {...props}
        className={`${stylingClassesForWhenAnyOptionIsSelected} rds-tertiary-button has-rds-radius-20`}
      />
    </div>
  );
};

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <DropdownArrow
        aria-label="Dropdown arrow"
        role="img"
        height={8}
        width={16}
      />
    </components.DropdownIndicator>
  );
};

/** Each component is keyed, and ships with default styles from react-select  {@link https://react-select.com/styles}.
 *  The component's default style object is passed as the first argument to the function when it's resolved.
 *  The second argument is the current state of the select, features like selectProps, isSelected etc. allowing you
 *  to implement dynamic styles for each of the components.
 **/
const customStyles = {
  control: (_: CSSProperties) => ({
    ..._,
  }),
  menu: (
    _: CSSProperties,
    { selectProps: { width } }: { selectProps: { width: number } },
  ) => ({
    ..._,
    width: width,
  }),
  menuList: (_: CSSProperties) => ({
    ..._,
    maxHeight: 150,
  }),
  option: (_: CSSProperties) => ({
    ..._,
    paddingBottom: 16,
    backgroundColor: 'transparent !important',
  }),
  placeholder: (_: CSSProperties) => null,
  valueContainer: (_: CSSProperties) => ({
    ..._,
    display: 'flex',
    padding: 0,
  }),
};

//this works for both for isMulti and for radio select
function assembleSelectDropdownLabel<T extends string>(
  values: SelectOption<T> | SelectOption<T>[],
  noValueText: string,
) {
  let count = 1;
  let selected;
  if (values instanceof Array) {
    count = values.length;
    selected = values[0];
  } else {
    selected = values;
  }
  if (!selected) return noValueText;
  return `${selected?.label} ${count > 1 ? `+${count - 1}` : ''}`;
}

export function SelectDropdown<T extends string>({
  refer,
  options = [],
  isMulti = false,
  label = '',
  defaultSelectedOption = undefined,
  className = '',
  onApply = undefined,
  onClear = undefined,
  ...restProps
}: any) {
  // TODO : Add SelectDropdownProps in support of react-select upgrade
  const [selectedOptions, setSelectedOptions] = useState(
    defaultSelectedOption ?? [],
  );

  const [placeholder, setPlaceholder] = useState(
    assembleSelectDropdownLabel(defaultSelectedOption, label),
  );

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const customComponents = {
    Menu,
    Option,
    Control,
    DropdownIndicator,
    IndicatorSeparator: false,
  };

  // Show the first selected option + <Others_count> in the input control value
  const handleOnChange = (
    selectedValues: SelectOption<T> | SelectOption<T>[],
  ) => {
    setSelectedOptions(selectedValues);
  };

  useEffect(() => {
    setPlaceholder(assembleSelectDropdownLabel(selectedOptions, label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);
  return (
    <>
      <label htmlFor={label} aria-label={label} />
      <StyledSelect
        ref={refer}
        data-testid="select-dropdown"
        //this data binding doesn't work at all with customComponents events, no rerender is triggered
        //it is required though for stylingClassesForWhenAnyOptionIsSelected to work correctly
        defaultValue={selectedOptions}
        options={options} // menu items list
        isMulti={isMulti} // allows to multi-select
        isSearchable={false} // disables search in control input
        isClearable={false} // disables reset/clear button in control input
        placeholder={placeholder}
        name={label}
        components={customComponents} // custom dropdown components
        styles={customStyles} // custom style object per component
        hideSelectedOptions={false} // to not hide selected options from the menu after selection
        controlShouldRenderValue={false} // hides selected options value in control input
        onChange={handleOnChange}
        classNamePrefix="select-dropdown"
        closeMenuOnSelect={false}
        tabSelectsValue={false}
        className={className}
        //this probably gives this overreactive dropdown select - is
        // there option to manipulate whether this fires or not for specific dropdown?
        //for multi it overreacts
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onClear={onClear}
        onMenuClose={() => {
          // added menuIsOpen to avoid multiple calls of onApply method.
          menuIsOpen && onApply(selectedOptions);
          setMenuIsOpen(false);
        }}
        id={label}
        menuPlacement="bottom"
        {...restProps}
      />
    </>
  );
}

export default SelectDropdown;
